import { LayoutDefinition, LayoutKind, Widget } from './types';

export const LAYOUT_CANVAS: Record<LayoutKind, { width: number; height: number }> = {
  simple: { width: 400, height: 300 },
  lvgl: { width: 800, height: 480 },
};

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function asWidgets(value: unknown): Widget[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item) => item && typeof item === 'object' && !Array.isArray(item)) as Widget[];
}

function inferKind(raw: Record<string, unknown>): LayoutKind {
  const kind = String(raw.kind || '').toLowerCase();
  if (kind === 'lvgl') {
    return 'lvgl';
  }
  if (kind === 'simple') {
    return 'simple';
  }
  const display = asObject(raw.display);
  const type = String(display.type || '').toLowerCase();
  if (type.includes('epd') || type.includes('uc8276')) {
    return 'simple';
  }
  if (type.includes('rgb') || type.includes('tft') || type.includes('lvgl')) {
    return 'lvgl';
  }
  return 'simple';
}

/** Demos used text; the old schema used box (Kasten). Live types: container, label, icon (+ LVGL button/switch/gauge/progress). */
export function canonicalWidgetType(raw: string | undefined): string {
  const type = String(raw || '').trim().toLowerCase();
  if (type === 'box' || type === 'kasten') {
    return 'container';
  }
  if (type === 'text') {
    return 'label';
  }
  if (type === 'progress_bar') {
    return 'progress';
  }
  return type || 'container';
}

function sanitizeWidget(widget: Widget): Widget {
  const next: Widget = {
    id: String(widget.id || '').trim(),
    type: canonicalWidgetType(widget.type),
    x: Number(widget.x) || 0,
    y: Number(widget.y) || 0,
    width: Number(widget.width) || 80,
    height: Number(widget.height) || 40,
  };
  if (widget.background) {
    next.background = String(widget.background);
  }
  if (widget.border) {
    next.border = true;
  }
  if (widget.color) {
    next.color = String(widget.color);
  }
  if (widget.text != null) {
    next.text = String(widget.text);
  }
  if (widget.align === 'center' || widget.align === 'right' || widget.align === 'left') {
    next.align = widget.align;
  }
  if (next.type === 'button' || next.type === 'switch') {
    if (widget.emit) {
      next.emit = String(widget.emit);
    }
  }
  if (Array.isArray(widget.children) && widget.children.length) {
    next.children = widget.children.map(sanitizeWidget);
  }
  return next;
}

export function widgetsOf(layout: LayoutDefinition): Widget[] {
  if (Array.isArray(layout.widgets) && layout.widgets.length > 0) {
    return layout.widgets;
  }
  return layout.layout?.zones || [];
}

export function allWidgets(widgets: Widget[]): Widget[] {
  const out: Widget[] = [];
  const walk = (list: Widget[]) => {
    for (const widget of list) {
      out.push(widget);
      if (widget.children && widget.children.length) {
        walk(widget.children);
      }
    }
  };
  walk(widgets);
  return out;
}

export function withWidgets(layout: LayoutDefinition, widgets: Widget[]): LayoutDefinition {
  return {
    ...layout,
    widgets,
    layout: { ...layout.layout, zones: widgets },
  };
}

export function setWidgetAt(widgets: Widget[], path: number[], next: Widget): Widget[] {
  if (!path.length) {
    return widgets;
  }
  const head = path[0];
  const rest = path.slice(1);
  return widgets.map((item, index) => {
    if (index !== head) {
      return item;
    }
    if (!rest.length) {
      return next;
    }
    return { ...item, children: setWidgetAt(item.children || [], rest, next) };
  });
}

export function removeWidgetAt(widgets: Widget[], path: number[]): Widget[] {
  if (!path.length) {
    return widgets;
  }
  if (path.length === 1) {
    return widgets.filter((_, index) => index !== path[0]);
  }
  const head = path[0];
  const rest = path.slice(1);
  return widgets.map((item, index) => {
    if (index !== head) {
      return item;
    }
    return { ...item, children: removeWidgetAt(item.children || [], rest) };
  });
}

export function nextWidgetId(widgets: Widget[], type: string): string {
  const prefix = `${type}_`;
  const used = new Set(allWidgets(widgets).map((widget) => String(widget.id || '')));
  let n = 1;
  while (used.has(`${prefix}${n}`)) {
    n += 1;
  }
  return `${prefix}${n}`;
}

export function getWidgetAt(widgets: Widget[], path: number[]): Widget | undefined {
  let list = widgets;
  let current: Widget | undefined;
  for (const index of path) {
    current = list[index];
    if (!current) {
      return undefined;
    }
    list = current.children || [];
  }
  return current;
}

/** Absolute top-left of the widget at path (sum of ancestor positions). */
export function absoluteOrigin(widgets: Widget[], path: number[]): { x: number; y: number } {
  let list = widgets;
  let x = 0;
  let y = 0;
  for (const index of path) {
    const widget = list[index];
    if (!widget) {
      break;
    }
    x += Number(widget.x) || 0;
    y += Number(widget.y) || 0;
    list = widget.children || [];
  }
  return { x, y };
}

function pathStartsWith(path: number[], prefix: number[]): boolean {
  if (prefix.length > path.length) {
    return false;
  }
  return prefix.every((value, index) => path[index] === value);
}

/**
 * Deepest container under (absX, absY), excluding the dragged widget and its
 * descendants (you cannot drop into yourself).
 */
export function findDropContainer(
  widgets: Widget[],
  absX: number,
  absY: number,
  excludePath: number[],
): number[] | null {
  const walk = (
    list: Widget[],
    ox: number,
    oy: number,
    prefix: number[],
  ): number[] | null => {
    for (let i = list.length - 1; i >= 0; i--) {
      const path = [...prefix, i];
      // Skip the dragged widget and anything nested inside it
      if (pathStartsWith(path, excludePath)) {
        continue;
      }

      const widget = list[i];
      const x = ox + (Number(widget.x) || 0);
      const y = oy + (Number(widget.y) || 0);
      const w = Number(widget.width) || 80;
      const h = Number(widget.height) || 40;
      if (absX < x || absY < y || absX >= x + w || absY >= y + h) {
        continue;
      }
      if (widget.type === 'container') {
        const deeper = walk(widget.children || [], x, y, path);
        if (deeper) {
          return deeper;
        }
        return path;
      }
      if (widget.children?.length) {
        const deeper = walk(widget.children, x, y, path);
        if (deeper) {
          return deeper;
        }
      }
    }
    return null;
  };
  return walk(widgets, 0, 0, []);
}

/**
 * Move widget from fromPath into toContainerPath (null = root), with local x/y.
 * Returns the new tree and the new path of the moved widget.
 */
export function reparentWidget(
  widgets: Widget[],
  fromPath: number[],
  toContainerPath: number[] | null,
  localX: number,
  localY: number,
): { widgets: Widget[]; newPath: number[] } {
  const moving = getWidgetAt(widgets, fromPath);
  if (!moving || !fromPath.length) {
    return { widgets, newPath: fromPath };
  }
  const nextWidget: Widget = {
    ...moving,
    x: Math.max(0, localX),
    y: Math.max(0, localY),
  };

  // Same parent: just update position
  const fromParent = fromPath.slice(0, -1);
  const sameParent = toContainerPath === null
    ? fromParent.length === 0
    : fromParent.join('.') === toContainerPath.join('.');
  if (sameParent) {
    return {
      widgets: setWidgetAt(widgets, fromPath, nextWidget),
      newPath: fromPath,
    };
  }

  let tree = removeWidgetAt(widgets, fromPath);

  // Adjust toContainerPath indices if we removed a sibling before the target
  let targetPath = toContainerPath;
  if (targetPath && targetPath.length && fromPath.length) {
    targetPath = adjustPathAfterRemove(targetPath, fromPath);
  }

  if (!targetPath || !targetPath.length) {
    const newPath = [tree.length];
    return { widgets: [...tree, nextWidget], newPath };
  }

  const parent = getWidgetAt(tree, targetPath);
  if (!parent || parent.type !== 'container') {
    const newPath = [tree.length];
    return { widgets: [...tree, nextWidget], newPath };
  }
  const children = [...(parent.children || []), nextWidget];
  const updatedParent = { ...parent, children };
  tree = setWidgetAt(tree, targetPath, updatedParent);
  const newPath = [...targetPath, children.length - 1];
  return { widgets: tree, newPath };
}

/** After removing fromPath, shift later sibling indices in targetPath. */
function adjustPathAfterRemove(targetPath: number[], fromPath: number[]): number[] {
  if (!targetPath.length || !fromPath.length) {
    return targetPath;
  }
  const out = [...targetPath];
  const parentLen = fromPath.length - 1;
  if (parentLen > out.length) {
    return out;
  }
  if (fromPath.slice(0, parentLen).join('.') !== out.slice(0, parentLen).join('.')) {
    return out;
  }
  const removedIndex = fromPath[parentLen];
  if (out.length > parentLen && out[parentLen] > removedIndex) {
    out[parentLen] -= 1;
  }
  return out;
}

export function datapointsOf(layout: LayoutDefinition): string[] {
  return allWidgets(widgetsOf(layout))
    .filter((widget) => ['label', 'icon', 'gauge', 'progress', 'switch'].includes(widget.type))
    .map((widget) => String(widget.id || '').trim())
    .filter(Boolean);
}

export function eventsOf(layout: LayoutDefinition): string[] {
  return allWidgets(widgetsOf(layout))
    .filter((widget) => widget.type === 'button' || widget.type === 'switch')
    .map((widget) => String(widget.emit || '').trim())
    .filter(Boolean);
}

export function normalizeLayout(raw: unknown): LayoutDefinition {
  const doc = asObject(raw);
  const metadata = asObject(doc.metadata);
  const display = asObject(doc.display);
  const nested = asObject(doc.layout);
  const kind = inferKind(doc);
  const fallback = LAYOUT_CANVAS[kind];
  const widgets = (asWidgets(doc.widgets).length ? asWidgets(doc.widgets) : asWidgets(nested.zones))
    .map(sanitizeWidget);
  return {
    kind,
    metadata: {
      name: String(metadata.name || '').trim() || 'Layout',
    },
    display: {
      width: Number(display.width) > 0 ? Number(display.width) : fallback.width,
      height: Number(display.height) > 0 ? Number(display.height) : fallback.height,
    },
    widgets,
    layout: { zones: widgets },
  };
}

export function serializeLayout(layout: LayoutDefinition): LayoutDefinition {
  const widgets = widgetsOf(layout).map(sanitizeWidget);
  return {
    kind: layout.kind === 'lvgl' ? 'lvgl' : 'simple',
    metadata: { name: String(layout.metadata?.name || '').trim() || 'Layout' },
    display: {
      width: Number(layout.display?.width) || LAYOUT_CANVAS.simple.width,
      height: Number(layout.display?.height) || LAYOUT_CANVAS.simple.height,
    },
    widgets,
    layout: { zones: widgets },
  };
}

export function applyKind(layout: LayoutDefinition, kind: LayoutKind): LayoutDefinition {
  const nextKind = kind === 'lvgl' ? 'lvgl' : 'simple';
  const prev = LAYOUT_CANVAS[layout.kind === 'lvgl' ? 'lvgl' : 'simple'];
  const sized = layout.display.width === prev.width && layout.display.height === prev.height;
  const allowed = new Set(toolboxTypesFor(nextKind));
  const widgets = filterWidgetsForKind(widgetsOf(layout), allowed);
  return withWidgets({
    ...layout,
    kind: nextKind,
    display: sized ? { ...LAYOUT_CANVAS[nextKind] } : layout.display,
  }, widgets);
}

/** Toolbox types. Both kinds support container hierarchy; LVGL adds richer widgets. */
export function toolboxTypesFor(kind: LayoutKind): string[] {
  if (kind === 'lvgl') {
    return ['container', 'label', 'icon', 'button', 'switch', 'gauge', 'progress'];
  }
  return ['container', 'label', 'icon'];
}

function filterWidgetsForKind(widgets: Widget[], allowed: Set<string>): Widget[] {
  return widgets
    .filter((widget) => allowed.has(canonicalWidgetType(widget.type)))
    .map((widget) => ({
      ...widget,
      type: canonicalWidgetType(widget.type),
      children: widget.children?.length
        ? filterWidgetsForKind(widget.children, allowed)
        : widget.children,
    }));
}
